import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  MenuItem,
  Tooltip,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

interface SweepableFieldProps {
  label: string;
  value: string;
  sweepValues: string[];
  isSweeping: boolean;
  onValueChange: (v: string) => void;
  onSweepValuesChange: (v: string[]) => void;
  onSweepToggle: () => void;
  placeholder?: string;
  type?: 'float' | 'int' | 'select';
  selectOptions?: readonly string[];
  disabled?: boolean;
  sweepModeEnabled?: boolean;
}

const SweepableField: React.FC<SweepableFieldProps> = ({
  label,
  value,
  sweepValues,
  isSweeping,
  onValueChange,
  onSweepValuesChange,
  onSweepToggle,
  placeholder,
  type = 'float',
  selectOptions,
  disabled,
  sweepModeEnabled,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const parseValue = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    if (type === 'select') return trimmed;
    const n = Number(trimmed);
    if (isNaN(n)) return null;
    if (type === 'int' && !Number.isInteger(n)) return null;
    return trimmed;
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addValues(inputValue);
    }
  };

  const addValues = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean);
    const valid: string[] = [];
    for (const part of parts) {
      const parsed = parseValue(part);
      if (parsed && !sweepValues.includes(parsed)) {
        valid.push(parsed);
      }
    }
    if (valid.length > 0) {
      onSweepValuesChange([...sweepValues, ...valid]);
      setError('');
    } else if (raw.trim()) {
      setError(`Invalid ${type} value`);
    }
    setInputValue('');
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addValues(inputValue);
    }
  };

  const removeValue = (idx: number) => {
    onSweepValuesChange(sweepValues.filter((_, i) => i !== idx));
  };

  // Non-sweep mode: render as normal TextField
  if (!isSweeping) {
    if (type === 'select' && selectOptions) {
      return (
        <Box sx={{ position: 'relative' }}>
          <TextField
            select
            fullWidth
            label={label}
            size="small"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
          >
            <MenuItem value="">Default</MenuItem>
            {selectOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          {sweepModeEnabled && (
            <Tooltip title="Enable sweep for this parameter">
              <IconButton
                size="small"
                onClick={onSweepToggle}
                sx={{
                  position: 'absolute',
                  right: 32,
                  top: 8,
                  opacity: 0.4,
                  '&:hover': { opacity: 1 },
                }}
              >
                <CompareArrowsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          label={label}
          size="small"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        />
        {sweepModeEnabled && (
          <Tooltip title="Enable sweep for this parameter">
            <IconButton
              size="small"
              onClick={onSweepToggle}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                opacity: 0.4,
                '&:hover': { opacity: 1 },
              }}
            >
              <CompareArrowsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Sweep mode
  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <TextField
          fullWidth
          label={`${label} (sweep)`}
          size="small"
          placeholder="Type values, press Enter or comma"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          error={!!error}
          helperText={error || `${sweepValues.length} value${sweepValues.length !== 1 ? 's' : ''}`}
          disabled={disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#EF8997' },
              '&:hover fieldset': { borderColor: '#EF8997' },
              '&.Mui-focused fieldset': { borderColor: '#EF8997' },
            },
          }}
        />
        <Tooltip title="Disable sweep for this parameter">
          <IconButton
            size="small"
            onClick={onSweepToggle}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#EF8997',
            }}
          >
            <CompareArrowsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {sweepValues.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {sweepValues.map((v, i) => (
            <Chip
              key={i}
              label={v}
              size="small"
              onDelete={() => removeValue(i)}
              sx={{
                backgroundColor: 'rgba(239, 137, 151, 0.15)',
                color: '#EF8997',
                '& .MuiChip-deleteIcon': { color: 'rgba(239, 137, 151, 0.5)' },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SweepableField;
